<template>
  <div class="questions inside-banner mobile-top collab">
    <collaborate-tour v-if="$store.state.tour && !loading" />

    <!-- ===== Search ===== -->
<banner-4>
  <h1 class="page-heading" style="color: #fff; font-size: 31px;">Questions</h1>

  <div class="input-wrap">
    <input
      id="collaborate-step-1"
      class="top-search mt0"
      type="text"
      placeholder="Search All Posts..."
      v-model="search"
      @keyup.enter.prevent="applySearch"
      @keyup.esc="clearSearch"
      style="padding: 15px 48px 15px 15px;"
    />

    <button
      v-if="search || appliedSearch"
      type="button"
      class="clear-btn"
      aria-label="Clear search"
      @click="clearSearch"
    >
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
        <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
      </svg>
    </button>
  </div>

  <div class="search-btns">
    <div class="right-side dflex">
      <button type="button" class="search" :disabled="!canSearch" @click="applySearch">
        <span class="material-icons">search</span> Search
      </button>
    </div>
  </div>
</banner-4>

    <div class="container collab-body">
      <div class="row">
        <!-- ===== Categories ===== -->
        <aside class="col-12 col-lg-3">
          <nav class="cats" aria-label="Categories">
            <h2 id="collaborate-step-2" class="cats__title">Categories</h2>
            <ul class="cats__list">
              <li v-for="cat in forumCats" :key="cat.id">
                <button
                  type="button"
                  class="cat"
                  :class="{ 'is-active': cat.id === selectedCat }"
                  :aria-current="cat.id === selectedCat ? 'true' : null"
                  @click="selectCategory(cat.id)"
                >
                  <span class="cat__dot" :style="{ background: catColor(cat.id).fg }"></span>
                  <span class="cat__name">{{ cat.name }}</span>
                  <span class="cat__count">{{ categoryCounts[cat.id] || 0 }}</span>
                </button>
              </li>
            </ul>
          </nav>
        </aside>

        <main class="col-12 col-lg-9">
          <!-- ===== Composer ===== -->
          <section class="c-card composer" :class="{ 'is-open': composerOpen }">
            <div class="composer__row">
              <avatar
                v-if="fullName"
                class="composer__avatar"
                color="#fff"
                :username="fullName"
                :backgroundColor="avatarColor(fullName)"
              />
              <input
                id="collaborate-step-3"
                ref="subject"
                v-model="postSubject"
                type="text"
                maxlength="150"
                class="composer__subject"
                placeholder="Start a discussion..."
                aria-label="Discussion subject"
                @focus="composerOpen = true"
              />
            </div>

            <transition name="expand">
              <div v-if="composerOpen" class="composer__body">
                <textarea
                  v-model="postContent"
                  rows="4"
                  class="composer__message"
                  placeholder="Add details, context, or your question..."
                  aria-label="Message"
                  @keydown.meta.enter.prevent="postNewQuestion"
                  @keydown.ctrl.enter.prevent="postNewQuestion"
                ></textarea>

                <p class="composer__label">Category</p>
                <div class="composer__cats" role="radiogroup" aria-label="Category">
                  <button
                    v-for="cat in postCategories"
                    :key="cat.id"
                    type="button"
                    role="radio"
                    class="pill pill--select"
                    :class="{ 'is-selected': postInsertCat === cat.id }"
                    :aria-checked="postInsertCat === cat.id"
                    :style="postInsertCat === cat.id ? pillStyle(cat.id) : null"
                    @click="postInsertCat = cat.id"
                  >
                    {{ cat.name }}
                  </button>
                </div>

                <div class="composer__footer">
                  <span class="composer__hint">{{ composerHint }}</span>
                  <button type="button" class="c-btn c-btn--ghost" @click="resetComposer">Cancel</button>
                  <button
                    type="button"
                    class="c-btn c-btn--primary"
                    :disabled="!canPost || posting"
                    @click="postNewQuestion"
                  >
                    <span v-if="posting" class="c-btn__spinner" aria-hidden="true"></span>
                    {{ posting ? "Posting..." : "Post" }}
                  </button>
                </div>
              </div>
            </transition>
          </section>

          <!-- ===== Toolbar ===== -->
          <div class="toolbar">
            <div>
              <h2 class="toolbar__title">{{ currentCategoryName }}</h2>
              <p class="toolbar__meta">
                <template v-if="loading">Loading discussions...</template>
                <template v-else>
                  {{ filteredQuestions.length }}
                  {{ filteredQuestions.length === 1 ? "discussion" : "discussions" }}
                </template>
              </p>
            </div>

            <div class="toolbar__actions">
              <!--
                <label class="sr-only" for="collab-sort">Sort by</label>
              <select id="collab-sort" v-model="sortModel" class="toolbar__sort">
                <option value="newest">Newest</option>
                <option value="active">Most comments</option>
              </select>
              
              -->
            

              <div id="collaborate-step-4" class="mini-pager" aria-label="Pagination">
                <button
                  type="button"
                  class="icon-btn"
                  :disabled="currPage <= 1"
                  aria-label="Previous page"
                  @click="setPage(currPage - 1)"
                >
                  <span class="material-icons">chevron_left</span>
                </button>
                <span class="mini-pager__label">{{ currPage }} / {{ pageCount }}</span>
                <button
                  type="button"
                  class="icon-btn"
                  :disabled="currPage >= pageCount"
                  aria-label="Next page"
                  @click="setPage(currPage + 1)"
                >
                  <span class="material-icons">chevron_right</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Active filters -->
          <div v-if="appliedSearch || selectedCat !== 0" class="filters">
            <button v-if="appliedSearch" type="button" class="filter-chip" @click="clearSearch">
              Search: "{{ appliedSearch }}"
              <span class="material-icons" aria-hidden="true">close</span>
            </button>
            <button v-if="selectedCat !== 0" type="button" class="filter-chip" @click="selectCategory(0)">
              {{ currentCategoryName }}
              <span class="material-icons" aria-hidden="true">close</span>
            </button>
            <button type="button" class="filters__reset" @click="resetFilters">Clear All</button>
          </div>

          <!-- ===== Loading skeleton ===== -->
          <ul v-if="loading" class="post-list" aria-hidden="true">
            <li v-for="n in 4" :key="n" class="c-card post post--skeleton">
              <span class="sk sk--avatar"></span>
              <div class="post__main">
                <span class="sk sk--pill"></span>
                <span class="sk sk--title"></span>
                <span class="sk sk--meta"></span>
              </div>
            </li>
          </ul>

          <!-- ===== Empty ===== -->
          <div v-else-if="!filteredQuestions.length" class="c-card empty">
            <span class="material-icons empty__icon" aria-hidden="true">forum</span>
            <h3 class="empty__title">{{ emptyTitle }}</h3>
            <p class="empty__text">{{ emptyText }}</p>
            <div class="empty__actions">
              <button
                v-if="appliedSearch || selectedCat !== 0"
                type="button"
                class="c-btn c-btn--ghost"
                @click="resetFilters"
              >
                Show all discussions
              </button>
              <button type="button" class="c-btn c-btn--primary" @click="startPost">Start a discussion</button>
            </div>
          </div>

          <!-- ===== Posts ===== -->
          <ul v-else class="post-list">
            <li v-for="(question, index) in pagedQuestions" :key="question.Tid">
              <router-link
                :id="index === 0 ? 'collaborate-step-5' : null"
                :to="`/questions/${question.Tid}`"
                class="c-card post"
              >
                <avatar
                  class="post__avatar"
                  color="#fff"
                  :username="question.author"
                  :backgroundColor="avatarColor(question.author)"
                />

                <div class="post__main">
                  <div class="post__tags">
                    <span class="pill" :style="pillStyle(question.Fid)">{{ question.Fname }}</span>
                    <span v-if="isNew(question)" class="pill pill--new">New</span>
                  </div>

                  <h3 class="post__title">{{ question.subject }}</h3>
                  <p v-if="question.excerpt" class="post__excerpt">{{ question.excerpt }}</p>

                  <p class="post__meta">
                    <span class="post__author">{{ question.author }}</span>
                    <template v-if="question.Name">
                      <span class="post__sep" aria-hidden="true">·</span>
                      <span>{{ question.Name }}</span>
                    </template>
                    <span class="post__sep" aria-hidden="true">·</span>
                    <time :datetime="question.Posted" :title="fullDate(question.Posted)">
                      {{ timeAgo(question.Posted) }}
                    </time>
                  </p>
                </div>

                <div
                  class="post__stats"
                  :class="{ 'is-empty': !question.commentCount }"
                  :aria-label="`${question.commentCount} comments`"
                >
                  <span class="material-icons" aria-hidden="true">chat_bubble_outline</span>
                  {{ question.commentCount }}
                </div>

                <span class="material-icons post__chevron" aria-hidden="true">chevron_right</span>
              </router-link>
            </li>
          </ul>

          <!-- ===== Bottom pagination ===== -->
          <nav v-if="!loading && pageCount > 1" class="pager" aria-label="Pagination">
            <button
              type="button"
              class="pager__btn"
              :disabled="currPage <= 1"
              @click="setPage(currPage - 1, true)"
            >
              <span class="material-icons">chevron_left</span> Prev
            </button>

            <template v-for="(p, i) in pageNumbers" :key="`${p}-${i}`">
              <span v-if="p === '...'" class="pager__gap">...</span>
              <button
                v-else
                type="button"
                class="pager__num"
                :class="{ 'is-active': p === currPage }"
                :aria-current="p === currPage ? 'page' : null"
                @click="setPage(p, true)"
              >
                {{ p }}
              </button>
            </template>

            <button
              type="button"
              class="pager__btn"
              :disabled="currPage >= pageCount"
              @click="setPage(currPage + 1, true)"
            >
              Next <span class="material-icons">chevron_right</span>
            </button>
          </nav>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Avatar from "../components/Atoms/Avatar.vue";
import CollaborateTour from "../components/Tours/CollaborateTour.vue";
import Banner4 from "../components/Banners/banner4.vue";
import { logoutOnTokenExpired } from "@/utils/auth";

const PAGE_SIZE = 25;

// Category accent colors: fg = text/dot, bg = soft tint
const CAT_COLORS = {
  0: { fg: "#4d5967", bg: "#eceef1" },
  1: { fg: "#4d5967", bg: "#eceef1" }, // Sales
  2: { fg: "#4d5967", bg: "#eceef1" }, // Products
  16: { fg: "#4d5967", bg: "#eceef1" }, // CET
  3: { fg: "#4d5967", bg: "#eceef1" }, // Configura
  4: { fg: "#4d5967", bg: "#eceef1" }, // Competitors
  6: { fg: "#4d5967", bg: "#eceef1" }, // Installation
  7: { fg: "#4d5967", bg: "#eceef1" }, // Announcements
  14: { fg: "#4d5967", bg: "#eceef1" }, // Museum
  5: { fg: "#4d5967", bg: "#eceef1" }, // Other
};

export default {
  name: "Questions",
  components: { Avatar, CollaborateTour, Banner4 },

  data() {
    return {
      server: import.meta.env.VITE_APP_SERVER_URL,
      questions: [],
      loading: true,
      currPage: 1,
      search: this.$route.query.q || "",

      composerOpen: false,
      postSubject: "",
      postContent: "",
      postInsertCat: null,
      posting: false,

      forumCats: [
        { id: 0, name: "All discussions" },
        { id: 1, name: "Sales" },
        { id: 2, name: "Products" },
        { id: 16, name: "CET" },
        { id: 3, name: "Configura" },
        { id: 4, name: "Competitors" },
        { id: 6, name: "Installation" },
        { id: 7, name: "Announcements" },
        { id: 14, name: "Museum" },
        { id: 5, name: "Other" },
      ],
    };
  },

  computed: {
    // ---- URL-driven state ----
    selectedCat() {
      const id = Number(this.$route.query.category);
      return Number.isFinite(id) ? id : 0;
    },
    appliedSearch() {
      return (this.$route.query.q || "").trim();
    },
    sortModel: {
      get() {
        return this.$route.query.sort === "active" ? "active" : "newest";
      },
      set(value) {
        this.updateQuery({ sort: value === "newest" ? null : value });
      },
    },

    // ---- User ----
    user() {
      return this.$store.state.user || {};
    },
    fullName() {
      return `${this.user.FirstName || ""} ${this.user.LastName || ""}`.trim();
    },
    colors() {
      return this.$store.state.colors || [];
    },

    // ---- Posts ----
    postCategories() {
      return this.forumCats.filter((c) => c.id !== 0);
    },
    currentCategoryName() {
      const cat = this.forumCats.find((c) => c.id === this.selectedCat);
      return cat ? cat.name : "All discussions";
    },
    normalizedQuestions() {
      return this.questions.map((q) => {
        const author = `${q.FirstName || ""} ${q.LastName || ""}`.trim();
        const subject = this.decodeHtml(q.Subject || "");
        const message = q.Message ? this.decodeHtml(String(q.Message)).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";
        return {
          ...q,
          author,
          subject,
          excerpt: message.length > 160 ? message.slice(0, 160).trimEnd() + "..." : message,
          commentCount: Math.max((Number(q.replies) || 1) - 1, 0),
          postedAt: q.Posted ? new Date(q.Posted).getTime() : 0,
          searchText: `${subject} ${author}`.toLowerCase(),
        };
      });
    },
    categoryCounts() {
      const counts = { 0: this.normalizedQuestions.length };
      this.normalizedQuestions.forEach((q) => {
        const id = Number(q.Fid);
        counts[id] = (counts[id] || 0) + 1;
      });
      return counts;
    },
    filteredQuestions() {
      const term = this.appliedSearch.toLowerCase();
      const list = this.normalizedQuestions.filter(
        (q) =>
          (this.selectedCat === 0 || Number(q.Fid) === this.selectedCat) &&
          (!term || q.searchText.includes(term))
      );
      return this.sortModel === "active"
        ? list.slice().sort((a, b) => b.commentCount - a.commentCount || b.postedAt - a.postedAt)
        : list.slice().sort((a, b) => b.postedAt - a.postedAt);
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredQuestions.length / PAGE_SIZE));
    },
    pagedQuestions() {
      const start = (this.currPage - 1) * PAGE_SIZE;
      return this.filteredQuestions.slice(start, start + PAGE_SIZE);
    },
    pageNumbers() {
      const total = this.pageCount;
      const cur = this.currPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      const start = Math.max(2, cur - 1);
      const end = Math.min(total - 1, cur + 1);
      if (start > 2) pages.push("...");
      for (let p = start; p <= end; p++) pages.push(p);
      if (end < total - 1) pages.push("...");
      pages.push(total);
      return pages;
    },
    emptyTitle() {
      if (this.appliedSearch) return `No results for "${this.appliedSearch}"`;
      if (this.selectedCat !== 0) return `No ${this.currentCategoryName} discussions yet`;
      return "No discussions yet";
    },
    emptyText() {
      if (this.appliedSearch) return "Try different keywords, or search all categories.";
      return "Be the first to start the conversation.";
    },

    // ---- Composer ----
    canSearch() {
      const term = this.search.trim();
      return term.length > 0 && term !== this.appliedSearch;
    },
    canPost() {
      return Boolean(this.postSubject.trim() && this.postContent.trim() && this.postInsertCat);
    },
    composerHint() {
      if (!this.postSubject.trim()) return "Add a subject";
      if (!this.postContent.trim()) return "Add a message";
      if (!this.postInsertCat) return "Pick a category";
      return "Ctrl + Enter to post";
    },
  },

  watch: {
    "$route.query"(next, prev) {
      // Reset paging when the filters change (not when only sort changes)
      if ((next.q || "") !== (prev.q || "") || (next.category || "") !== (prev.category || "")) {
        this.currPage = 1;
      }
      this.search = next.q || "";
    },
    sortModel() {
      this.currPage = 1;
    },
    pageCount(count) {
      if (this.currPage > count) this.currPage = count;
    },
  },

  created() {
    this.$store.commit("showNavigation");
    this.$store.commit("setActivePage", "collaborate");
  },

  mounted() {
    this.loadQuestions();
    this.$store.commit("SET_VIEWED_COLLABORATE");
  },

  methods: {
    authConfig() {
      return {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
        },
      };
    },

    async loadQuestions() {
      const cached = this.$store.state.collaborate.questions || [];
      if (cached.length) {
        this.questions = cached;
        this.loading = false;
      }
      if (!localStorage.getItem("SSCUSER")) {
        this.loading = false;
        return;
      }
      try {
        const { data } = await axios.get(`${this.server}/api/v1/questions`, this.authConfig());
        this.questions = data;
        this.$store.commit("UPDATE_COLLABORATE", data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          logoutOnTokenExpired();
        } else if (!cached.length) {
          this.$toast.error("Couldn't load discussions. Please refresh the page.");
        }
        console.error("Error fetching questions:", error);
      } finally {
        this.loading = false;
      }
    },

    async postNewQuestion() {
      if (!this.canPost || this.posting) return;
      this.posting = true;

      const postDate = new Date();
      postDate.setMinutes(postDate.getMinutes() - 1);

      // The server should derive the user from the JWT and the IP from the request.
      const postData = {
        UserIp: null,
        Subject: this.postSubject.trim(),
        Message: this.postContent.trim(),
        EditedBy: this.user.UserName,
        fullName: this.fullName,
        Topic: this.postInsertCat,
        UserId: this.user.UserId,
        Date: postDate,
      };

      try {
        const { data } = await axios.post(`${this.server}/api/v1/questions`, postData, this.authConfig());
        this.questions = data;
        this.$store.commit("UPDATE_COLLABORATE", data);
        this.resetComposer();
        this.resetFilters();
        this.$toast.success("Your discussion is live!", { hideProgressBar: false, icon: false });
      } catch (error) {
        if (error.response && error.response.status === 401) {
          logoutOnTokenExpired();
          return;
        }
        this.$toast.error("There was a problem posting. Your draft is still here, so please try again.");
        console.error("Error posting question:", error);
      } finally {
        this.posting = false;
      }
    },

    // ---- Composer ----
    startPost() {
      this.composerOpen = true;
      if (this.selectedCat !== 0) this.postInsertCat = this.selectedCat;
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.$nextTick(() => this.$refs.subject && this.$refs.subject.focus());
    },
    cancelPost() {
      const hasDraft = this.postSubject.trim() || this.postContent.trim();
      if (hasDraft && !window.confirm("Discard this draft?")) return;
      this.resetComposer();
    },
    resetComposer() {
      this.postSubject = "";
      this.postContent = "";
      this.postInsertCat = null;
      this.composerOpen = false;
    },

    // ---- Filters (stored in the URL) ----
    updateQuery(changes) {
      const query = { ...this.$route.query, ...changes };
      Object.keys(query).forEach((k) => {
        if (query[k] === null || query[k] === "" || query[k] === undefined) delete query[k];
      });
      this.$router.push({ query }).catch(() => {});
    },
    selectCategory(id) {
      this.updateQuery({ category: id === 0 ? null : String(id) });
    },
    applySearch() {
      if (!this.canSearch) return;
      this.updateQuery({ q: this.search.trim() });
    },
    clearSearch() {
      this.search = "";
      if (this.appliedSearch) this.updateQuery({ q: null });
    },
    resetFilters() {
      this.search = "";
      this.updateQuery({ q: null, category: null });
    },
    setPage(page, scrollUp = false) {
      if (page < 1 || page > this.pageCount) return;
      this.currPage = page;
      if (scrollUp) window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // ---- Display helpers ----
    catColor(id) {
      return CAT_COLORS[Number(id)] || CAT_COLORS[0];
    },
    pillStyle(id) {
      const c = this.catColor(id);
      return { color: c.fg, background: c.bg, borderColor: c.bg };
    },
    decodeHtml(html) {
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    },
    avatarColor(name) {
      if (!this.colors.length || !name) return undefined;
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
      return this.colors[hash % this.colors.length];
    },
    timeAgo(date) {
      return date ? this.$moment(date).subtract(1, "minutes").fromNow() : "Unknown date";
    },
    fullDate(date) {
      return date ? this.$moment(date).format("MMM D, YYYY [at] h:mm A") : "";
    },
    isNew(question) {
      return (
        this.$store.state.newPostCount > 0 &&
        Boolean(this.user.LastActivity) &&
        this.$moment(question.Posted).isAfter(this.user.LastActivity)
      );
    },
  },
};
</script>

<style scoped>
.collab {
  --teal: #429da8;
  --teal-dark: #35838c;
  --teal-soft: #e6f3f4;
  --slate: #4d5967;
  --ink: #1e3040;
  --text: #3d4752;
  --muted: #7b8591;
  --line: #e4e8ec;
  --surface: #ffffff;
  --page: #f4f6f8;
  --radius: 10px;
  --shadow: 0 1px 2px rgba(30, 48, 64, 0.06), 0 1px 3px rgba(30, 48, 64, 0.08);
  --shadow-hover: 0 4px 14px rgba(30, 48, 64, 0.12);
  color: var(--text);
}
.collab-body {
  padding-top: 24px;
  padding-bottom: 48px;
}
.c-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

/* ===== Search ===== */
.searchbar {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 12px;
  padding: 6px 6px 6px 16px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  margin: 10px 0;
}
.searchbar__icon {
  color: var(--muted);
}
.searchbar__input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  font-size: 16px;
  padding: 10px 4px;
  color: var(--ink);
  background: transparent;
}
.searchbar__clear {
  border: 0;
  background: transparent;
  color: var(--muted);
  display: flex;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
}
.searchbar__clear:hover {
  background: var(--page);
}
.searchbar__submit {
  border: 0;
  background: var(--slate);
  color: #fff;
  font-weight: 500;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}
.searchbar__submit:hover:not(:disabled) {
  background: var(--ink);
}
.searchbar__submit:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ===== Categories ===== */
.cats {
  position: sticky;
  top: 90px;
}
.cats__title {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 4px 12px 10px;
}
.cats__list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.cat {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: var(--text);
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.cat:hover {
  background: rgba(77, 89, 103, 0.08);
}
.cat.is-active {
  background: var(--teal-soft);
  color: var(--ink);
  font-weight: 600;
}
.cat__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cat__name {
  flex: 1;
}
.cat__count {
  font-size: 12px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.cat.is-active .cat__count {
  color: var(--teal-dark);
}

@media (max-width: 991px) {
  .cats {
    position: static;
    margin: 0 -15px 16px;
  }
  .cats__title {
    display: none;
  }
  .cats__list {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 0 15px 4px;
    scrollbar-width: none;
  }
  .cats__list::-webkit-scrollbar {
    display: none;
  }
  .cat {
    width: auto;
    white-space: nowrap;
    border: 1px solid var(--line);
    background: var(--surface);
    border-radius: 999px;
    padding: 7px 14px;
    font-size: 14px;
  }
  .cat.is-active {
    border-color: var(--teal);
  }
}

/* ===== Composer ===== */
.composer {
  padding: 14px 16px;
  margin-bottom: 20px;
  transition: box-shadow 0.2s, border-color 0.2s;
}
.composer.is-open {
  /*
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(66, 157, 168, 0.15), var(--shadow);
  */
}
.composer__row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.composer__avatar {
  flex-shrink: 0;
}
.composer__subject {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--line);
  background: var(--page);
  border-radius: 4px;
  padding: 14px;
  font-size: 16px;
  color: var(--ink);
  outline: 0;
  transition: background 0.15s, border-color 0.15s;
}
.composer__subject:hover {
  border-color: #cfd6dd;
}
.composer.is-open .composer__subject {
  background: #fff;
  border-radius: 8px;
  font-weight: 600;
}
.composer__body {
  margin-top: 12px;
}
.composer__message {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.5;
  resize: vertical;
  outline: 0;
  color: var(--text);
}
.composer__message:focus,
.composer__subject:focus {
  /*border-color: var(--teal);*/
}
.composer__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  margin: 12px 0 8px;
}
.composer__cats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.composer__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--line);
}
.composer__hint {
  margin-right: auto;
  font-size: 13px;
  color: var(--muted);
}

/* ===== Pills ===== */
.pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.pill--select {
  font-size: 13px;
  font-weight: 500;
  padding: 7px 12px;
  background: #fff;
  border-color: var(--line);
  color: var(--text);
  cursor: pointer;
  transition: all 0.15s;
}
.pill--select:hover {
  border-color: #cfd6dd;
}
.pill--select.is-selected {
  font-weight: 600;
}
.pill--new {
  background: var(--teal);
  color: #fff;
}

/* ===== Buttons ===== */
.c-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}
.c-btn--primary {
  background: var(--teal);
  color: #fff;
}
.c-btn--primary:hover:not(:disabled) {
  background: var(--teal-dark);
}
.c-btn--primary:disabled {
  opacity: 0.45;
  cursor: default;
}
.c-btn--ghost {
  background: transparent;
  color: var(--slate);
}
.c-btn--ghost:hover {
  background: var(--page);
}
.c-btn__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--slate);
  cursor: pointer;
}
.icon-btn:hover:not(:disabled) {
  background: var(--page);
}
.icon-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ===== Toolbar ===== */
.toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.toolbar__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  margin: 0;
}
.toolbar__meta {
  margin: 2px 0 0;
  font-size: 14px;
  color: var(--muted);
}
.toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar__sort {
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 14px;
  color: var(--text);
  cursor: pointer;
}
.mini-pager {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 2px;
}
.mini-pager__label {
  font-size: 13px;
  color: var(--muted);
  padding: 0 6px;
  font-variant-numeric: tabular-nums;
}

/* ===== Active filters ===== */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--teal);
  background: var(--teal);
  color: #fff;
  border-radius: 999px;
  padding: 4px 8px 4px 12px;
  font-size: 13px;
  cursor: pointer;
}
.filter-chip .material-icons {
  font-size: 16px;
}
.filters__reset {
  border: 0;
  background: none;
  color: var(--teal-dark);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

/* ===== Posts ===== */
.post-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.post {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  color: inherit;
  text-decoration: none;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.15s;
}
.post:hover,
.post:focus-visible {
  text-decoration: none;
  color: inherit;
  border-color: #cfd6dd;
  box-shadow: var(--shadow-hover);
  transform: translateY(-1px);
}
.post:focus-visible {
  outline: 2px solid var(--teal);
  outline-offset: 2px;
}
.post__avatar {
  flex-shrink: 0;
}
.post__main {
  flex: 1;
  min-width: 0;
}
.post__tags {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}
.post__title {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
  margin: 0 0 4px;
  overflow-wrap: anywhere;
}
.post:hover .post__title {
  color: var(--teal-dark);
}
.post__excerpt {
  font-size: 14px;
  line-height: 1.5;
  color: var(--text);
  margin: 0 0 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.post__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--muted);
  margin: 0;
}
.post__author {
  color: var(--text);
  font-weight: 600;
}
.post__sep {
  opacity: 0.6;
}
.post__stats {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
  color: var(--slate);
  background: var(--page);
  border-radius: 999px;
  padding: 5px 10px;
  align-self: center;
  font-variant-numeric: tabular-nums;
}
.post__stats .material-icons {
  font-size: 16px;
}
.post__stats.is-empty {
  background: transparent;
  color: var(--muted);
  font-weight: 400;
}
.post__chevron {
  align-self: center;
  color: #c3cad1;
  transition: color 0.15s, transform 0.15s;
}
.post:hover .post__chevron {
  color: var(--teal);
  transform: translateX(2px);
}

@media (max-width: 575px) {
  .post {
    padding: 14px;
    gap: 10px;
  }
  .post__avatar,
  .post__chevron {
    display: none;
  }
  .toolbar__actions {
    width: 100%;
    justify-content: space-between;
  }
}

/* ===== Skeleton ===== */
.post--skeleton {
  pointer-events: none;
}
.sk {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f4 25%, #f6f8fa 50%, #eef1f4 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}
.sk--avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sk--pill {
  width: 70px;
  height: 18px;
  border-radius: 999px;
  margin-bottom: 10px;
}
.sk--title {
  width: 75%;
  height: 18px;
  margin-bottom: 10px;
}
.sk--meta {
  width: 40%;
  height: 12px;
}

/* ===== Empty ===== */
.empty {
  text-align: center;
  padding: 48px 24px;
}
.empty__icon {
  font-size: 44px;
  color: var(--teal);
  background: var(--teal-soft);
  border-radius: 50%;
  padding: 16px;
}
.empty__title {
  font-size: 19px;
  font-weight: 700;
  color: var(--ink);
  margin: 16px 0 6px;
}
.empty__text {
  color: var(--muted);
  margin: 0 0 20px;
}
.empty__actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ===== Bottom pager ===== */
.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 24px;
  flex-wrap: wrap;
}
.pager__btn,
.pager__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--slate);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.pager__btn:hover:not(:disabled),
.pager__num:hover {
  background: #fff;
  border-color: var(--line);
}
.pager__btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.pager__num.is-active {
  background: var(--teal);
  color: #fff;
  font-weight: 700;
}
.pager__gap {
  color: var(--muted);
  padding: 0 4px;
}

/* ===== Motion ===== */
.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
@keyframes shimmer {
  to {
    background-position: -200% 0;
  }
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .collab *,
  .collab *::before,
  .collab *::after {
    animation: none !important;
    transition: none !important;
  }
}

/* ===== Search clear (X) ===== */
.input-wrap {
  position: relative;
}
.input-wrap .top-search {
  width: 100%;
}
.clear-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.clear-btn:hover {
  background: var(--page);
  color: var(--ink);
}
.clear-btn:focus-visible {
  outline: 2px solid var(--teal);
  outline-offset: 1px;
}
</style>
